import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const FORM_SELECTOR = 'form[data-cc-ajax-cart-form]';

function getCartSectionIds() {
  const sectionIds = new Set();

  for (const component of document.querySelectorAll('cart-items-component')) {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      sectionIds.add(component.dataset.sectionId);
    }
  }

  return Array.from(sectionIds);
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

  return response.json();
}

function openCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer || typeof drawer.open !== 'function') return;

  drawer.open();
}

function setFormBusy(form, busy) {
  const button = form.querySelector('button[type="submit"]');
  if (!(button instanceof HTMLButtonElement)) return;

  button.disabled = busy;
  button.toggleAttribute('aria-busy', busy);
}

function dispatchCartError(target, error, detail) {
  target.dispatchEvent(
    new CartErrorEvent({
      error: error?.message || 'Add to cart failed',
      code: 'INVALID',
      detail,
    })
  );
}

async function handleAjaxCartSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;
  if (!form.checkValidity()) return;

  event.preventDefault();

  const formData = new FormData(form);
  const variantId = formData.get('id');
  const quantity = Number(formData.get('quantity')) || 1;
  const sectionIds = getCartSectionIds();
  const deferredEventPromise = CartLinesUpdateEvent.createPromise();

  if (sectionIds.length > 0) {
    formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);
  }

  setFormBusy(form, true);

  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [
        {
          merchandiseId: variantId ? String(variantId) : '',
          quantity,
        },
      ],
      promise: deferredEventPromise.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });
    const result = await response.json();

    if (!response.ok || result.status) {
      const message = result.description || result.message || `Add to cart failed: ${response.status}`;
      const error = new Error(message);
      dispatchCartError(form, error, {
        description: result.description,
        errors: result.errors,
      });

      const cart = await fetchCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: true,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cc-ajax-cart',
        },
      });

      return;
    }

    const cart = await fetchCart();
    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        itemCount: cart.item_count,
        sections: result.sections,
        source: 'cc-ajax-cart',
      },
    });

    requestAnimationFrame(openCartDrawer);
  } catch (error) {
    console.error(error);
    deferredEventPromise.reject(error);
    dispatchCartError(form, error);
  } finally {
    setFormBusy(form, false);
  }
}

document.addEventListener('submit', handleAjaxCartSubmit);
